import React, { useState, useEffect } from 'react';
import { Mail, X } from 'lucide-react';

export default function NewsletterPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Check if user already subscribed
    const isSubscribed = localStorage.getItem('newsletter_subscribed');

    if (!isSubscribed) {
      // Show after 3 seconds
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');

    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${apiBase}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (res.status === 429) { 


        setStatus('error');


        return;


      }


      if (res.ok) {
        setStatus('success');
        setMessage('Thank you for subscribing! We will keep you updated.');
        localStorage.setItem('newsletter_subscribed', 'true');
        // Auto-close after 3 seconds on success
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        setStatus('error');
        setMessage(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Network error. Please try again later.');
    }
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'fadeIn 0.4s ease-out'
    }}>
      <div className="glass-card newsletter-modal" style={{
        position: 'relative',
        width: '90%',
        maxWidth: '450px',
        padding: '40px 30px',
        backgroundColor: '#FEF8F1',
        border: '1px solid rgba(212,175,55,0.3)',
        borderRadius: '16px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
        textAlign: 'center',
        animation: 'slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1)'
      }}>
        {/* Close Button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            padding: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--primary-gold)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
        >
          <X size={20} />
        </button>

        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(212,175,55,0.2) 0%, rgba(184,146,37,0.05) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px auto',
          border: '1px solid var(--primary-gold-border)'
        }}>
          <Mail size={28} color="var(--primary-gold)" />
        </div>

        <h3 style={{
          color: 'var(--primary-gold)',
          fontFamily: 'var(--font-heading)',
          fontSize: '1.6rem',
          marginBottom: '10px'
        }}>Stay Spiritually Connected</h3>

        <p style={{
          color: '#000000ff',
          fontSize: '0.95rem',
          lineHeight: '1.6',
          marginBottom: '25px',
          opacity: 0.9
        }}>
          Subscribe to our newsletter for exclusive updates on new sanctuaries, wellness retreats, and spiritual wisdom.
        </p>

        {status === 'success' ? (
          <div style={{
            padding: '15px',
            backgroundColor: 'rgba(37, 211, 102, 0.1)',
            border: '1px solid rgba(37, 211, 102, 0.3)',
            borderRadius: '8px',
            color: '#25D366',
            fontSize: '0.9rem',
            fontWeight: '500'
          }}>
            {message}
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  backgroundColor: 'rgba(0,0,0,0.03)',
                  border: '1px solid rgba(212,175,55,0.4)',
                  borderRadius: '8px',
                  color: '#333',
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--primary-gold)'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(212,175,55,0.3)'}
                disabled={status === 'loading'}
              />
            </div>

            {status === 'error' && (
              <div style={{ color: '#ff6b6b', fontSize: '0.85rem', textAlign: 'left', marginTop: '-5px' }}>
                {message}
              </div>
            )}

            <button
              type="submit"
              className="btn-gold"
              disabled={status === 'loading'}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '0.95rem',
                letterSpacing: '0.1em',
                fontWeight: '600',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                opacity: status === 'loading' ? 0.7 : 1,
                cursor: status === 'loading' ? 'not-allowed' : 'pointer'
              }}
            >
              {status === 'loading' ? 'SUBSCRIBING...' : 'SUBSCRIBE NOW'}
            </button>
          </form>
        )}

        <div style={{ marginTop: '20px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          By subscribing, you agree to our Privacy Policy. We never spam.
        </div>
      </div>

      <style>{`
        @media (max-width: 480px) {
          .newsletter-modal { padding: 24px 16px !important; }
        }
      `}</style>
    </div>
  );
}
